import {SlidersIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, type IconButtonProps, Tooltip} from '@primer/react'
import {clsx} from 'clsx'

import {
  getReasoningEffortLabel,
  REASONING_EFFORT_OPTIONS,
  type ReasoningEffort,
} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import {usePickerTelemetry} from '../telemetry/use-picker-telemetry'
import styles from './ReasoningEffortPicker.module.css'

const REASONING_EFFORT_PICKER_TELEMETRY = {
  opened: 'reasoning_effort_picker_opened',
  changed: 'reasoning_effort_changed',
  dismissed: 'reasoning_effort_picker_dismissed',
} as const
const DEFAULT_EFFORT = 'default'

interface ReasoningEffortPickerProps {
  selectedReasoningEffort?: ReasoningEffort
  onUpdateReasoningEffort: (reasoningEffort: ReasoningEffort | undefined) => void
  // The reasoning efforts the selected model supports
  supportedReasoningEfforts: readonly string[] | undefined
  // Collapse the trigger to just the icon when horizontal space is tight.
  iconOnly?: boolean
  /**
   * Opts the trigger into the composer's container-query compaction.
   * The selected level stays visible while the dropdown caret can be hidden.
   */
  responsiveIconOnly?: boolean
  triggerClassName?: string
  disabled?: boolean
  tooltipDirection?: IconButtonProps['tooltipDirection']
}

export function ReasoningEffortPicker({
  selectedReasoningEffort,
  onUpdateReasoningEffort,
  supportedReasoningEfforts,
  iconOnly = false,
  responsiveIconOnly = false,
  triggerClassName,
  disabled = false,
  tooltipDirection,
}: ReasoningEffortPickerProps) {
  const {onOpenChange: trackOpenChange, onPicked: trackPicked} = usePickerTelemetry(REASONING_EFFORT_PICKER_TELEMETRY)
  const fromEffort = selectedReasoningEffort ?? DEFAULT_EFFORT
  const selectEffort = (next: ReasoningEffort | undefined) => {
    const toEffort = next ?? DEFAULT_EFFORT
    trackPicked(fromEffort, toEffort, {from_reasoning_effort: fromEffort, to_reasoning_effort: toEffort})
    onUpdateReasoningEffort(next)
  }
  // Hide until we know the model supports reasoning
  if (!supportedReasoningEfforts || supportedReasoningEfforts.length === 0) {
    return null
  }

  const options = REASONING_EFFORT_OPTIONS.filter(option => supportedReasoningEfforts.includes(option.value))
  const getEffortLabel = (effort?: ReasoningEffort) => getReasoningEffortLabel(effort)

  // No explicit selection is "Default": send no override and let the backend pick the
  // model's own default effort (model-specific, not always medium).
  const triggerLabel = `Reasoning effort: ${getEffortLabel(selectedReasoningEffort)}`

  const textButton = (
    <ActionMenu.Button
      className={clsx(styles.menuButton, triggerClassName)}
      variant="invisible"
      size="medium"
      aria-label={triggerLabel}
      disabled={disabled}
      data-responsive-effort-control={responsiveIconOnly ? '' : undefined}
    >
      <span className={styles.buttonName}>{getEffortLabel(selectedReasoningEffort)}</span>
    </ActionMenu.Button>
  )

  return (
    <ActionMenu onOpenChange={open => trackOpenChange(open, {from_reasoning_effort: fromEffort})}>
      {iconOnly ? (
        // Icon-only (tight layouts, e.g. the global chat overlay): a square IconButton with
        // no dropdown caret or label padding. Its aria-label supplies both the accessible
        // name and the hover tooltip for the selected effort.
        <ActionMenu.Anchor>
          <IconButton
            icon={SlidersIcon}
            aria-label={triggerLabel}
            variant="invisible"
            size="medium"
            disabled={disabled}
            tooltipDirection={tooltipDirection}
          />
        </ActionMenu.Anchor>
      ) : responsiveIconOnly && !disabled ? (
        // Primer's Tooltip has to wrap `ActionMenu.Button` directly — ActionMenu
        // matches on that identity to thread its anchor props — so this is
        // inline rather than a helper component. It is skipped while disabled,
        // where TooltipV2 throws on a native disabled button.
        <Tooltip text="Reasoning effort" direction={tooltipDirection}>
          {textButton}
        </Tooltip>
      ) : (
        textButton
      )}
      <ActionMenu.Overlay width="small" align="end">
        <ActionList selectionVariant="single">
          <div className={styles.menuHeading}>Reasoning effort</div>
          <ActionList.Item selected={selectedReasoningEffort === undefined} onSelect={() => selectEffort(undefined)}>
            Default
            <ActionList.Description variant="block">
              Let the model pick its default reasoning effort
            </ActionList.Description>
          </ActionList.Item>
          {options.map(option => (
            <ActionList.Item
              key={option.value}
              selected={option.value === selectedReasoningEffort}
              onSelect={() => selectEffort(option.value)}
            >
              <span>{option.label}</span>
              <ActionList.Description variant="block">{option.description}</ActionList.Description>
            </ActionList.Item>
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
